// MaturaArithmeticTripletParamOpenTask.js
// Zadanie maturalne otwarte: parametr m w trzywyrazowym ciagu arytmetycznym.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  parseFractionLikeInput,
} from "../../../../components/inputs/FractionInput";
import EquationInput from "../../../../components/inputs/EquationInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";


const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

function signed(n) {
  if (n === 0) return "";
  return n > 0 ? `+${n}` : `-${Math.abs(n)}`;
}

function linearLatex(a, b) {
  const aPart =
    a === 1 ? "m" : a === -1 ? "-m" : `${a}m`;
  return `${aPart}${signed(b)}`;
}

function quadraticLatex(c) {
  return `m^2${signed(c)}`;
}

function factorLatex(n) {
  return n < 0 ? `(${n})` : String(n);
}

function numberLatex(value) {
  if (Number.isInteger(value)) return String(value);
  for (const denominator of [2, 4]) {
    if (Math.abs(value * denominator - Math.round(value * denominator)) < 1e-9) {
      const numerator = Math.round(value * denominator);
      const sign = numerator < 0 ? "-" : "";
      return `${sign}\\frac{${Math.abs(numerator)}}{${denominator}}`;
    }
  }
  return String(value).replace(".", "{,}");
}

function polyLatex(S, constant) {
  const linearCoeff = -S;
  const linearPart =
    linearCoeff === 0
      ? ""
      : linearCoeff === 1
      ? "+m"
      : linearCoeff === -1
      ? "-m"
      : `${signed(linearCoeff)}m`;

  return `2m^2${linearPart}${signed(constant)}`;
}

function getRaw(value) {
  if (value && typeof value === "object" && "raw" in value) {
    return value.raw ?? "";
  }
  return String(value ?? "");
}

function parseNumberLike(value) {
  if (value && typeof value === "object" && Number.isFinite(value.parsed)) {
    return value.parsed;
  }
  const raw = getRaw(value);
  const parsed = parseFractionLikeInput(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function evalExpressionForM(expr, m) {
  const safe = String(expr || "")
    .replace(/,/g, ".")
    .replace(/[−–]/g, "-")
    .replace(/\s+/g, "")
    .replace(/\^/g, "**")
    .replace(/(\d)(m)/g, "$1*$2")
    .replace(/(m)(\d)/g, "$1*$2")
    .replace(/(\))(?=m|\d|\()/g, ")*")
    .replace(/(m|\d)(?=\()/g, "$1*");

  if (!/^[0-9m+\-*/().*]+$/.test(safe)) return null;

  try {
    // eslint-disable-next-line no-new-func
    const value = Function("m", `"use strict"; return (${safe});`)(m);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function equationDiffValue(raw, m) {
  const text = getRaw(raw);
  if (!text.trim()) return null;
  const parts = text.split("=");
  if (parts.length >= 2) {
    const left = parts[0];
    const right = parts.slice(1).join("=");
    const l = evalExpressionForM(left, m);
    const r = evalExpressionForM(right, m);
    if (l == null || r == null) return null;
    return l - r;
  }
  return evalExpressionForM(text, m);
}

function expectedDiffValue(data, m) {
  return 2 * m * m - data.sumSlope * m + data.polyConstant;
}

function equationEquivalent(raw, data) {
  const text = getRaw(raw).trim();
  if (!text) return false;

  const samples = [-5, -3, -1, 0, 1, 2, 4, 6, data.m0, data.badM];
  const pairs = [];

  for (const m of samples) {
    const userVal = equationDiffValue(text, m);
    const expectedVal = expectedDiffValue(data, m);
    if (userVal == null) return false;

    if (Math.abs(expectedVal) < 1e-8) {
      if (Math.abs(userVal) > 1e-5) return false;
    } else {
      pairs.push({ userVal, expectedVal });
    }
  }

  const first = pairs.find((p) => Math.abs(p.userVal) > 1e-8);
  if (!first) return false;
  const scale = first.userVal / first.expectedVal;

  return pairs.every(
    ({ userVal, expectedVal }) =>
      Math.abs(userVal - scale * expectedVal) < 1e-4
  );
}

function sameNumber(a, b) {
  return a != null && Math.abs(a - b) < 0.01;
}

function buildTaskData() {
  for (let attempt = 0; attempt < 200; attempt += 1) {
    const m0 = randInt(1, 7);
    const badAbs = randInt(1, 6);
    const direction = pick(["decreasing", "increasing"]);
    const rAbs = randInt(1, 7);
    const r = direction === "decreasing" ? -rAbs : rAbs;
    const center = randInt(-8, 18);
    const middleConst = center - m0 * m0;

    if (Math.abs(middleConst) > 35) continue;

    const firstValue = center - r;
    const secondValue = center;
    const thirdValue = center + r;

    const sumSlope = 2 * (m0 - badAbs);
    const firstSlope = pick([-5, -4, -3, -2, -1, 1, 2, 3, 4, 5]);
    const thirdSlope = sumSlope - firstSlope;
    if (thirdSlope === 0 || Math.abs(thirdSlope) > 8) continue;

    const firstConst = firstValue - firstSlope * m0;
    const thirdConst = thirdValue - thirdSlope * m0;
    if (Math.max(Math.abs(firstConst), Math.abs(thirdConst)) > 40) continue;

    const badM = -badAbs;
    const badFirst = firstSlope * badM + firstConst;
    const badSecond = badM * badM + middleConst;
    const badThird = thirdSlope * badM + thirdConst;
    const badDiff = badSecond - badFirst;

    if (badSecond - badFirst !== badThird - badSecond) continue;
    if (direction === "decreasing" && badDiff < 0) continue;
    if (direction === "increasing" && badDiff > 0) continue;

    return {
      m0,
      badM,
      direction,
      firstSlope,
      firstConst,
      middleConst,
      thirdSlope,
      thirdConst,
      firstValue,
      secondValue,
      thirdValue,
      badFirst,
      badSecond,
      badThird,
      goodDiff: r,
      badDiff,
      sumSlope,
      polyConstant: -2 * m0 * badAbs,
      maxPoints: 3,
    };
  }

  return {
    m0: 2,
    badM: -2,
    direction: "decreasing",
    firstSlope: 2,
    firstConst: 3,
    middleConst: 0,
    thirdSlope: -2,
    thirdConst: 5,
    firstValue: 7,
    secondValue: 4,
    thirdValue: 1,
    badFirst: -1,
    badSecond: 4,
    badThird: 9,
    goodDiff: -3,
    badDiff: 5,
    sumSlope: 0,
    polyConstant: -8,
    maxPoints: 3,
  };
}

function generateMay2025Original() {
  return {
    type: "MaturaArithmeticTripletParamOpenTask",
    data: {
      m0: 5 / 2,
      badM: -2,
      direction: "decreasing",
      firstSlope: 2,
      firstConst: 11,
      middleConst: 3,
      thirdSlope: -1,
      thirdConst: 5,
      firstValue: 16,
      secondValue: 37 / 4,
      thirdValue: 5 / 2,
      badFirst: 7,
      badSecond: 7,
      badThird: 7,
      goodDiff: -27 / 4,
      badDiff: 0,
      sumSlope: 1,
      polyConstant: -10,
      maxPoints: 3,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 15,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return generateMay2025Original();
  }

  const data = buildTaskData();
  return {
    type: "MaturaArithmeticTripletParamOpenTask",
    data,
  };
}

export function renderTask({ data }) {
  const directionText =
    data.direction === "decreasing" ? "malejący" : "rosnący";

  return (
    <div style={{ textAlign: "left" }} className="space-y-3">
      <p>
        <strong>Zadanie maturalne - otwarte (0-3 pkt).</strong>
      </p>
      <p>
        Wyznacz wartość <InlineMath math={"m"} />, dla której trzywyrazowy ciąg
      </p>
      <BlockMath
        math={`\\left(${linearLatex(
          data.firstSlope,
          data.firstConst
        )},\\;${quadraticLatex(data.middleConst)},\\;${linearLatex(
          data.thirdSlope,
          data.thirdConst
        )}\\right)`}
      />
      <p>
        jest arytmetyczny i {directionText}. Zapisz obliczenia.
      </p>
    </div>
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const ua = userAnswer || {};
  const setField = (key) => (val) => onChange({ ...(ua || {}), [key]: val });
  const eqRaw = getRaw(ua.equation);

  return (
    <div style={{ display: "grid", gap: 14, maxWidth: 620 }}>
      <OpenTaskScoringHint variant="independent" />
      <div style={{ display: "grid", gap: 6 }}>
        <div>
          <strong>Krok 1.</strong> Zapisz równanie wynikające z warunku, że ciąg
          jest arytmetyczny:
        </div>
        <EquationInput
          value={ua.equation}
          onChange={setField("equation")}
          disabled={disabled}
          placeholder="np. 2(m^2+3)=(2m+11)+(5-m)"
          showPreview={false}
        />
        {eqRaw && (
          <div>
            <strong>Podgląd:</strong> <InlineMath math={eqRaw} />
          </div>
        )}
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        <div>
          <strong>Krok 2.</strong> Podaj wszystkie wartości{" "}
          <InlineMath math={"m"} />, dla których ciąg jest arytmetyczny:
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "grid", gap: 4 }}>
            <InlineMath math={"m_1 ="} />
            <FractionInput
              label="m_1"
              value={ua.m1}
              onChange={setField("m1")}
              disabled={disabled}
              placeholder="np. -2"
            />
          </div>
          <div style={{ display: "grid", gap: 4 }}>
            <InlineMath math={"m_2 ="} />
            <FractionInput
              label="m_2"
              value={ua.m2}
              onChange={setField("m2")}
              disabled={disabled}
              placeholder="np. 5/2"
            />
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gap: 6 }}>
        <div>
          <strong>Krok 3.</strong> Wybierz wartość{" "}
          <InlineMath math={"m"} />, która spełnia warunek, że ciąg jest{" "}
          {data.direction === "decreasing" ? "malejący" : "rosnący"}:
        </div>
        <FractionInput
          label="m"
          value={ua.finalM}
          onChange={setField("finalM")}
          disabled={disabled}
          placeholder="np. 2 albo 5/2"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const ua = userAnswer || {};
  const equationOk = equationEquivalent(ua.equation, data);

  const m1 = parseNumberLike(ua.m1);
  const m2 = parseNumberLike(ua.m2);
  const candidates = [m1, m2];
  const candidatesOk =
    candidates.some((v) => sameNumber(v, data.m0)) &&
    candidates.some((v) => sameNumber(v, data.badM));

  const finalM = parseNumberLike(ua.finalM);
  const finalMOk = sameNumber(finalM, data.m0);

  // Dwie poprawne wartości m są wynikiem rozwiązania właściwego równania
  // kwadratowego. Jeżeli zapis równania zawiera oczywistą literówkę, ale oba
  // jego rozwiązania są poprawne, uznajemy punkt za metodę. Dzięki temu błąd
  // zapisu nie odbiera punktu za prawidłowo przeprowadzony dalszy rachunek.
  const equationStepOk = equationOk || candidatesOk;

  let score = 0;
  if (equationStepOk) score += 1;
  if (candidatesOk) score += 1;
  if (finalMOk) score += 1;

  const maxScore = data.maxPoints ?? 3;
  return {
    isCorrect: score === maxScore,
    score,
    maxScore,
    parts: {
      "równanie": equationStepOk,
      "wartości m": candidatesOk,
      "wybór po monotoniczności": finalMOk,
    },
    message: !equationOk && candidatesOk
      ? "W zapisie równania jest literówka lub błąd zapisu, ale punkt za metodę został uznany na podstawie obu poprawnych rozwiązań."
      : null,
    messageType: !equationOk && candidatesOk ? "warning" : null,
  };
}

export function renderAnswer({ data }) {
  const poly = polyLatex(data.sumSlope, data.polyConstant);
  return (
    <div style={{ display: "grid", gap: 4, textAlign: "left" }}>
      <div>
        Równanie: <InlineMath math={`${poly}=0`} />
      </div>
      <div>
        Kandydaci:{" "}
        <InlineMath
          math={`m=${numberLatex(data.m0)}\\quad\\text{lub}\\quad m=${numberLatex(
            data.badM
          )}`}
        />
      </div>
      <div>
        Odpowiedź końcowa:{" "}
        <InlineMath math={`m=${numberLatex(data.m0)}`} />
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) {
    return <i>(brak odpowiedzi)</i>;
  }
  const eq = getRaw(userAnswer.equation) || "\\text{--}";
  const m1 = getRaw(userAnswer.m1) || "\\text{--}";
  const m2 = getRaw(userAnswer.m2) || "\\text{--}";
  const finalM = getRaw(userAnswer.finalM) || "\\text{--}";

  return (
    <div style={{ display: "grid", gap: 4, textAlign: "left" }}>
      <div>
        <InlineMath math={`\\text{równanie: } ${eq}`} />
      </div>
      <div>
        <InlineMath math={`m_1=${m1},\\quad m_2=${m2}`} />
      </div>
      <div>
        <InlineMath math={`m=${finalM}`} />
      </div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const directionText =
    data.direction === "decreasing" ? "malejący" : "rosnący";
  const oppositeText =
    data.direction === "decreasing" ? "nie jest malejący" : "nie jest rosnący";

  const first = linearLatex(data.firstSlope, data.firstConst);
  const second = quadraticLatex(data.middleConst);
  const third = linearLatex(data.thirdSlope, data.thirdConst);
  const poly = polyLatex(data.sumSlope, data.polyConstant);

  return (
    <div style={{ textAlign: "left" }} className="space-y-3">
      <p>
        <strong>Krok 1.</strong> Dla trzech kolejnych wyrazów ciągu
        arytmetycznego środkowy wyraz jest średnią arytmetyczną skrajnych:
      </p>
      <BlockMath math={`2a_2=a_1+a_3`} />

      <p>
        <strong>Krok 2.</strong> Podstawiamy wyrazy z treści zadania:
      </p>
      <BlockMath math={`2(${second})=(${first})+(${third})`} />
      <BlockMath math={`${poly}=0`} />
      <BlockMath
        math={`2\\left(m-${numberLatex(
          data.m0
        )}\\right)\\left(m+${numberLatex(Math.abs(data.badM))}\\right)=0`}
      />
      <BlockMath
        math={`m=${numberLatex(data.m0)}\\quad\\text{lub}\\quad m=${numberLatex(
          data.badM
        )}`}
      />

      <p>
        <strong>Krok 3.</strong> Sprawdzamy warunek, że ciąg ma być{" "}
        {directionText}.
      </p>
      <p>
        Dla <InlineMath math={`m=${numberLatex(data.m0)}`} /> otrzymujemy:
      </p>
      <BlockMath
        math={`(${numberLatex(data.firstValue)},\\;${numberLatex(
          data.secondValue
        )},\\;${numberLatex(data.thirdValue)})`}
      />
      <BlockMath math={`r=${numberLatex(data.goodDiff)}`} />
      <p>
        Ten ciąg jest {directionText}.
      </p>

      <p>
        Dla <InlineMath math={`m=${numberLatex(data.badM)}`} /> otrzymujemy:
      </p>
      <BlockMath
        math={`(${data.badFirst},\\;${data.badSecond},\\;${data.badThird})`}
      />
      <BlockMath math={`r=${numberLatex(data.badDiff)}`} />
      <p>
        Ten ciąg {oppositeText}, więc odrzucamy tę wartość parametru.
      </p>

      <p>
        Zatem <InlineMath math={`m=${numberLatex(data.m0)}`} />.
      </p>
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  renderUserAnswer,
  checkAnswer,
  renderAnswer,
  renderExplanation,
};
